import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { useGetGateway } from 'src/features/gateway/api/useGetGateway';
import GatewayForm from 'src/features/gateway/components/form/GatewayForm';
import { gatewayFormSchema, GatewayFormSchemaValues } from 'src/features/gateway/utils/formSchema';
import { Gateway } from 'src/features/gateway/utils/types';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';

interface GatewayFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedGateway?: Gateway;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: GatewayFormSchemaValues,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
}

function GatewayFormModal<TData>({
    selectedGateway,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: GatewayFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { parkingId } = useParking();

    // We are in edit mode if we have a selected site
    // otherwise it means we are creating a new site

    const { data: gateway } = useGetGateway({
        gatewayId: selectedGateway?.id ?? '',
        parkingId,
        // We only want to fetch if we're in edit
        config: { enabled: !!selectedGateway?.id && isModalOpen },
    });

    const onError = useOnError();

    const defaultValues = React.useMemo(
        () =>
            gateway
                ? {
                      ...gateway,
                      entry_zone: gateway.entry_zone?.id,
                      exit_zone: gateway.exit_zone?.id,
                  }
                : {
                      translations: [],
                      required_identifier_types: [],
                      visitor_categories: undefined,
                      entry_zone: null,
                      exit_zone: null,
                  },
        [gateway],
    );

    const form = useForm<GatewayFormSchemaValues>({
        resolver: zodResolver(gatewayFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [gateway, isModalOpen, form, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: GatewayFormSchemaValues) => {
            const dirtyFields = form.formState.dirtyFields;

            const editedFields = Object.keys(dirtyFields).reduce(
                (acc, key) => {
                    const typedKey = key as keyof GatewayFormSchemaValues;
                    acc[typedKey] = values[typedKey];
                    return acc;
                },
                {} as Record<keyof GatewayFormSchemaValues, any>,
            );

            mutate(
                editedFields,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },
        [mutate, form, onError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={className}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <GatewayForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedGateway && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default GatewayFormModal;
